/**
 * Headless Practice Mode session logic.
 *
 * Keeps all practice state and the answer -> hint -> next-step -> solution
 * ladder out of the DOM so it can be unit-tested directly. The screen renders
 * from this session and dispatches user actions into it.
 *
 * Recovery ladder for an incorrect answer:
 *   attempt again -> conceptual hint -> reveal next step -> ... -> full solution
 * The session records whether each question was correct on the FIRST attempt
 * with no help, which is the signal mastery relies on.
 */

import type { Difficulty, Question, QuestionSignature } from "../topics/types";
import type { TopicId } from "../curriculum/curriculum";
import { getGenerator } from "../topics/registry";
import { gradeSubmission } from "../engine/answer";
import { makeSeed } from "../engine/rng";

export interface PracticeConfig {
  readonly topic: TopicId;
  readonly difficulty: Difficulty;
  readonly count: number;
}

export interface QuestionOutcome {
  readonly question: Question;
  attempts: number;
  hintsUsed: number;
  stepsRevealed: number;
  solutionRevealed: boolean;
  correct: boolean;
  /** Correct on the first attempt with no hints/steps/solution shown. */
  firstAttemptCorrect: boolean;
}

export interface SubmitResult {
  readonly correct: boolean;
  /** True once the question is finished (correct, or solution revealed). */
  readonly settled: boolean;
}

/** How many recent signatures to remember for duplicate avoidance. */
const RECENT_WINDOW = 12;

export class PracticeSession {
  readonly config: PracticeConfig;
  private readonly outcomes: QuestionOutcome[] = [];
  private readonly recent: QuestionSignature[] = [];
  private index = 0;

  constructor(config: PracticeConfig) {
    this.config = config;
    if (!getGenerator(config.topic)) {
      throw new Error(`no generator for topic ${config.topic}`);
    }
    this.outcomes.push(this.makeOutcome());
  }

  /** 1-based position for display. */
  get position(): number {
    return this.index + 1;
  }
  get total(): number {
    return this.config.count;
  }
  get current(): QuestionOutcome {
    return this.outcomes[this.index] as QuestionOutcome;
  }
  get isLast(): boolean {
    return this.index >= this.config.count - 1;
  }
  get isComplete(): boolean {
    return this.index >= this.config.count;
  }

  private makeOutcome(): QuestionOutcome {
    const gen = getGenerator(this.config.topic);
    if (!gen) throw new Error(`no generator for topic ${this.config.topic}`);
    const q = gen.generate({
      seed: makeSeed(),
      difficulty: this.config.difficulty,
      recent: [...this.recent],
    });
    const sig = gen.signature(q);
    this.recent.push(sig);
    if (this.recent.length > RECENT_WINDOW) this.recent.shift();
    return {
      question: q,
      attempts: 0,
      hintsUsed: 0,
      stepsRevealed: 0,
      solutionRevealed: false,
      correct: false,
      firstAttemptCorrect: false,
    };
  }

  /** Grade a submitted answer for the current question. */
  submit(raw: string): SubmitResult {
    const o = this.current;
    if (o.correct || o.solutionRevealed) {
      return { correct: o.correct, settled: true };
    }
    const noHelpYet =
      o.attempts === 0 &&
      o.hintsUsed === 0 &&
      o.stepsRevealed === 0 &&
      !o.solutionRevealed;
    o.attempts++;
    const correct = gradeSubmission(raw, o.question.answer);
    if (correct) {
      o.correct = true;
      if (noHelpYet) o.firstAttemptCorrect = true;
      return { correct: true, settled: true };
    }
    return { correct: false, settled: false };
  }

  /** Reveal the next conceptual hint; returns it or null when exhausted. */
  useHint(): string | null {
    const o = this.current;
    const hints = o.question.hints;
    if (o.hintsUsed >= hints.length) return null;
    const hint = hints[o.hintsUsed];
    o.hintsUsed++;
    return hint ? hint.text : null;
  }

  get hintsRemaining(): number {
    return this.current.question.hints.length - this.current.hintsUsed;
  }

  /** Reveal the next worked step; returns it or null when exhausted. */
  revealNextStep(): { description: string; latex: string } | null {
    const o = this.current;
    const steps = o.question.steps;
    if (o.stepsRevealed >= steps.length) return null;
    const step = steps[o.stepsRevealed];
    o.stepsRevealed++;
    return step ? { description: step.description, latex: step.latex } : null;
  }

  get stepsRemaining(): number {
    return this.current.question.steps.length - this.current.stepsRevealed;
  }

  /** Reveal the full solution: all steps, marks the question settled. */
  revealSolution(): ReadonlyArray<{ description: string; latex: string }> {
    const o = this.current;
    o.solutionRevealed = true;
    o.stepsRevealed = o.question.steps.length;
    return o.question.steps.map((s) => ({
      description: s.description,
      latex: s.latex,
    }));
  }

  /** Advance to the next question. Returns false if the session is finished. */
  next(): boolean {
    if (this.isComplete) return false;
    this.index++;
    if (this.isComplete) return false;
    if (!this.outcomes[this.index]) this.outcomes.push(this.makeOutcome());
    return true;
  }

  /** Summary once complete (or at any point). */
  summary(): {
    total: number;
    answered: number;
    correct: number;
    firstAttempt: number;
    hintsUsed: number;
    solutionsRevealed: number;
    outcomes: readonly QuestionOutcome[];
  } {
    const answered = this.outcomes.filter(
      (o) => o.correct || o.solutionRevealed,
    ).length;
    const correct = this.outcomes.filter((o) => o.correct).length;
    const firstAttempt = this.outcomes.filter((o) => o.firstAttemptCorrect).length;
    const hintsUsed = this.outcomes.reduce((s, o) => s + o.hintsUsed, 0);
    const solutionsRevealed = this.outcomes.filter((o) => o.solutionRevealed).length;
    return {
      total: this.config.count,
      answered,
      correct,
      firstAttempt,
      hintsUsed,
      solutionsRevealed,
      outcomes: this.outcomes,
    };
  }
}
